import { localize, siteContent, type Locale, type LocalizedText } from "@/content/site";

export type ProofBarItem = {
  code: string;
  label: string;
  value: string;
};

export type TrustPillarItem = {
  title: LocalizedText;
  desc: LocalizedText;
};

export type MethodologyStep = {
  title: LocalizedText;
  desc: LocalizedText;
};

export type WorkPattern = {
  slug: string;
  title: LocalizedText;
  businessSituation: LocalizedText;
  workflowProblem: LocalizedText;
  systemPattern: LocalizedText;
  dataRequired: readonly LocalizedText[];
  productionControls: readonly LocalizedText[];
  metricsToBaseline: readonly LocalizedText[];
  firstPhase: LocalizedText;
  links: readonly {
    href: string;
    label: LocalizedText;
  }[];
};

export const proofTrustCopy = {
  work: {
    heroLabel: {
      en: "Work patterns",
      fr: "Schémas de travail",
    },
    heroTitle: {
      en: "How production AI work becomes credible before it scales.",
      fr: "Comment un mandat IA devient crédible avant de passer à l’échelle.",
    },
    heroSubtitle: {
      en: "Vert AI uses scoped work patterns, baselines, evaluation, monitoring, human review, and governance to turn AI, data, and decision opportunities into production systems buyers can inspect.",
      fr: "Vert AI utilise des schémas de travail cadrés, des bases de mesure, l’évaluation, le monitoring, la revue humaine et la gouvernance pour transformer les opportunités IA, données et décision en systèmes de production inspectables.",
    },
    methodologyEyebrow: {
      en: "Methodology",
      fr: "Méthodologie",
    },
    methodologyTitle: {
      en: "How proof is created before scale.",
      fr: "Comment la preuve est créée avant l’échelle.",
    },
    methodologyIntro: {
      en: "The sequence is intentionally narrow: pick the expensive workflow, make the baseline visible, confirm AI is the right mechanism, build the smallest production-grade system, and expand only after trust is earned.",
      fr: "La séquence est volontairement ciblée : choisir le flux coûteux, rendre la base de mesure visible, confirmer que l’IA est le bon mécanisme, construire le plus petit système de production utile, puis élargir seulement après avoir gagné la confiance.",
    },
    patternsEyebrow: {
      en: "Anonymized patterns",
      fr: "Schémas anonymisés",
    },
    patternsTitle: {
      en: "Common operating patterns Vert AI helps turn into production systems.",
      fr: "Schémas opérationnels courants que Vert AI aide à transformer en systèmes de production.",
    },
    patternsIntro: {
      en: "Vert AI shows its work through methodology, delivery standards, and production patterns: scoped workflows, clear baselines, evaluation, monitoring, human review, governance, and measurable operating impact.",
      fr: "Vert AI démontre sa rigueur par sa méthode, ses standards de livraison et ses schémas de production : flux cadrés, base de mesure claire, évaluation, monitoring, revue humaine, gouvernance et impact opérationnel mesurable.",
    },
    trustEyebrow: {
      en: "Trust pillars",
      fr: "Piliers de confiance",
    },
    trustTitle: {
      en: "The controls a buyer should expect to inspect.",
      fr: "Les contrôles qu’un acheteur devrait pouvoir inspecter.",
    },
    trustIntro: {
      en: "Production trust is not a claim. It is the visible operating layer around the system: controls, ownership, review, monitoring, and measurement.",
      fr: "La confiance en production n’est pas une affirmation. C’est la couche opérationnelle visible autour du système : contrôles, responsabilité, revue, monitoring et mesure.",
    },
    founderEyebrow: {
      en: "Founder experience",
      fr: "Expérience fondateur",
    },
    founderTitle: {
      en: "Founder-led production experience.",
      fr: "Expérience de production dirigée par le fondateur.",
    },
    founderNote: {
      en: "The same operating discipline carries into new work: scoped workflows, clear baselines, evaluation, observability, human review, governance, and business measurement.",
      fr: "La même discipline opérationnelle guide les nouveaux mandats : flux cadrés, base de mesure claire, évaluation, observabilité, revue humaine, gouvernance et mesure business.",
    },
    surfacesEyebrow: {
      en: "Next buying step",
      fr: "Prochaine étape d’achat",
    },
    surfacesTitle: {
      en: "Use the proof system to choose the right conversation.",
      fr: "Utiliser le système de preuve pour choisir la bonne conversation.",
    },
    ctaHeadline: {
      en: "Bring the pattern you need to make measurable.",
      fr: "Amenez le schéma que vous devez rendre mesurable.",
    },
    ctaDesc: {
      en: "A fit call should clarify whether the work is ready for a production AI system, a data foundation pass, a narrower workflow automation, or no build yet.",
      fr: "Un appel de cadrage devrait clarifier si le travail est prêt pour un système IA de production, un chantier de fondation données, une automatisation de flux plus ciblée ou aucun build pour l’instant.",
    },
  },
} as const;

export const trustPillars = [
  {
    title: { en: "Evaluation", fr: "Évaluation" },
    desc: {
      en: "Expected behavior, edge cases, failure modes, and acceptance criteria are defined before launch.",
      fr: "Comportement attendu, cas limites, modes d’échec et critères d’acceptation sont définis avant le lancement.",
    },
  },
  {
    title: { en: "Observability", fr: "Observabilité" },
    desc: {
      en: "Usage, quality, latency, exceptions, drift, incidents, and business signals need visible monitoring.",
      fr: "Usage, qualité, latence, exceptions, dérive, incidents et signaux business doivent être suivis visiblement.",
    },
  },
  {
    title: { en: "Human review", fr: "Revue humaine" },
    desc: {
      en: "People stay in control where risk, judgment, customer impact, or commercial accountability matters.",
      fr: "Les humains restent en contrôle lorsque le risque, le jugement, l’impact client ou l’imputabilité commerciale comptent.",
    },
  },
  {
    title: { en: "Governance", fr: "Gouvernance" },
    desc: {
      en: "Ownership, documentation, change control, and review cadence are part of the system design.",
      fr: "Responsabilité, documentation, contrôle des changements et cadence de revue font partie du design système.",
    },
  },
  {
    title: { en: "Data quality", fr: "Qualité des données" },
    desc: {
      en: "Source definitions, transformations, freshness, ownership, permissions, and missing data are made inspectable.",
      fr: "Définitions source, transformations, fraîcheur, responsabilité, permissions et données manquantes sont rendues inspectables.",
    },
  },
  {
    title: { en: "Security and privacy", fr: "Sécurité et confidentialité" },
    desc: {
      en: "Access, permissions, sensitive data handling, security review, and vendor exposure are scoped before automation expands.",
      fr: "Accès, permissions, données sensibles, revue sécurité et exposition fournisseur sont cadrés avant d’élargir l’automatisation.",
    },
  },
  {
    title: { en: "ROI measurement", fr: "Mesure du ROI" },
    desc: {
      en: "Baseline metrics, adoption, cycle time, quality, and operating impact are measured before scale.",
      fr: "Métriques de départ, adoption, temps de cycle, qualité et impact opérationnel sont mesurés avant l’échelle.",
    },
  },
] as const satisfies readonly TrustPillarItem[];

export const methodologySteps = [
  {
    title: { en: "Select expensive workflow", fr: "Choisir le flux coûteux" },
    desc: {
      en: "Start where delay, manual effort, inconsistency, or decision risk already has operational cost.",
      fr: "Commencer là où les délais, l’effort manuel, l’incohérence ou le risque décisionnel ont déjà un coût opérationnel.",
    },
  },
  {
    title: { en: "Define baseline", fr: "Définir la base de mesure" },
    desc: {
      en: "Document the current cycle time, exception rate, review effort, quality issues, adoption constraints, and business impact.",
      fr: "Documenter le temps de cycle actuel, le taux d’exception, l’effort de revue, les problèmes de qualité, les contraintes d’adoption et l’impact business.",
    },
  },
  {
    title: { en: "Expose data readiness", fr: "Exposer la préparation des données" },
    desc: {
      en: "Map sources, ownership, definitions, quality gaps, permissions, and missing instrumentation.",
      fr: "Cartographier sources, responsabilités, définitions, écarts qualité, permissions et instrumentation manquante.",
    },
  },
  {
    title: { en: "Design workflow/system controls", fr: "Concevoir contrôles de flux et système" },
    desc: {
      en: "Define system boundaries, escalation paths, human review, audit trails, and fallback behavior.",
      fr: "Définir limites système, chemins d’escalade, revue humaine, traces d’audit et comportements de repli.",
    },
  },
  {
    title: { en: "Evaluate with human review", fr: "Évaluer avec revue humaine" },
    desc: {
      en: "Test against real examples, edge cases, acceptance criteria, and review queues before release.",
      fr: "Tester avec exemples réels, cas limites, critères d’acceptation et files de revue avant publication.",
    },
  },
  {
    title: { en: "Deploy with observability", fr: "Déployer avec observabilité" },
    desc: {
      en: "Launch with monitoring for usage, quality, exceptions, latency, ownership, and business metrics.",
      fr: "Lancer avec monitoring de l’usage, de la qualité, des exceptions, de la latence, de la responsabilité et des métriques business.",
    },
  },
  {
    title: { en: "Expand only after trust is earned", fr: "Élargir seulement après confiance gagnée" },
    desc: {
      en: "Scale after users trust the system, controls work, and the measured workflow improves.",
      fr: "Étendre après que les utilisateurs font confiance au système, que les contrôles tiennent et que le flux mesuré s’améliore.",
    },
  },
] as const satisfies readonly MethodologyStep[];

export const workPatterns = [
  {
    slug: "spreadsheet-reporting-to-decision-intelligence",
    title: {
      en: "From spreadsheet-driven reporting to decision intelligence",
      fr: "Du reporting par tableurs à l’intelligence décisionnelle",
    },
    businessSituation: {
      en: "Leadership depends on recurring spreadsheets, manual reconciliations, and meeting-by-meeting interpretation to decide where to act.",
      fr: "La direction dépend de tableurs récurrents, de rapprochements manuels et d’interprétations en réunion pour décider où agir.",
    },
    workflowProblem: {
      en: "The reporting workflow produces numbers, but the decision logic, exceptions, owners, and next actions are not encoded anywhere reliable.",
      fr: "Le flux de reporting produit des chiffres, mais la logique décisionnelle, les exceptions, les responsables et les prochaines actions ne sont pas formalises de façon fiable.",
    },
    systemPattern: {
      en: "A governed decision layer that combines metric definitions, source-of-truth data, exception logic, and recommended actions for recurring reviews.",
      fr: "Une couche décisionnelle gouvernée combinant définitions de métriques, données source de vérité, logique d’exception et actions recommandées pour les revues récurrentes.",
    },
    dataRequired: [
      { en: "Reporting spreadsheets and source extracts", fr: "Tableurs de reporting et extractions sources" },
      { en: "Metric definitions and business rules", fr: "Définitions de métriques et règles business" },
      { en: "Decision cadence, owners, and escalation paths", fr: "Cadence décisionnelle, responsables et chemins d’escalade" },
    ],
    productionControls: [
      { en: "Metric dictionary and lineage checks", fr: "Dictionnaire de métriques et contrôles de lignage" },
      { en: "Exception thresholds and review workflow", fr: "Seuils d’exception et flux de revue" },
      { en: "Change log for definitions and rules", fr: "Journal des changements de définitions et règles" },
    ],
    metricsToBaseline: [
      { en: "Reporting cycle time", fr: "Temps de cycle de reporting" },
      { en: "Manual reconciliation effort", fr: "Effort de rapprochement manuel" },
      { en: "Decision latency after reporting", fr: "Latence décisionnelle après reporting" },
    ],
    firstPhase: {
      en: "A source-of-truth model for one decision review, a metric dictionary, exception logic, and a working decision surface for the operating team.",
      fr: "Un modèle source de vérité pour une revue décisionnelle, un dictionnaire de métriques, une logique d’exception et une surface décisionnelle utilisable par l’équipe opérationnelle.",
    },
    links: [
      { href: "/services/decision-intelligence", label: { en: "Explore decision intelligence systems", fr: "Explorer les systèmes d’intelligence décisionnelle" } },
      { href: "/services/data-platforms", label: { en: "Review AI-ready data foundations", fr: "Voir les fondations de données pour l’IA" } },
      { href: "/contact", label: { en: "Discuss a reporting-to-decision workflow", fr: "Discuter d’un flux reporting vers décision" } },
    ],
  },
  {
    slug: "manual-support-triage-to-human-reviewed-ai-workflow",
    title: {
      en: "From manual support triage to human-reviewed AI workflow",
      fr: "Du triage support manuel à un flux IA avec revue humaine",
    },
    businessSituation: {
      en: "Support or operations teams spend significant time classifying requests, gathering context, drafting responses, and routing exceptions.",
      fr: "Les équipes support ou opérations passent beaucoup de temps à classifier les demandes, rassembler le contexte, rédiger des réponses et router les exceptions.",
    },
    workflowProblem: {
      en: "Triage depends on individual judgment, queue discipline, and copy-paste work, which makes quality, speed, and escalation hard to manage.",
      fr: "Le triage dépend du jugement individuel, de la discipline de file et du copier-coller, ce qui rend la qualité, la vitesse et l’escalade difficiles à gérer.",
    },
    systemPattern: {
      en: "An AI-assisted triage workflow that classifies requests, retrieves approved context, drafts next steps, and routes uncertain cases for human review.",
      fr: "Un flux de triage assisté par IA qui classifie les demandes, récupère le contexte approuvé, rédige les prochaines étapes et route les cas incertains vers une revue humaine.",
    },
    dataRequired: [
      { en: "Historical tickets, emails, chats, or intake records", fr: "Tickets, courriels, clavardages ou dossiers d’entrée historiques" },
      { en: "Approved knowledge base, policies, and escalation rules", fr: "Base de connaissances, politiques et règles d’escalade approuvées" },
      { en: "Queue categories, priorities, outcomes, and review notes", fr: "Catégories de files, priorités, résultats et notes de revue" },
    ],
    productionControls: [
      { en: "Confidence thresholds and human review queues", fr: "Seuils de confiance et files de revue humaine" },
      { en: "Audit logs for classification and suggested actions", fr: "Journaux d’audit pour classification et actions suggérées" },
      { en: "Approved-source retrieval and response boundaries", fr: "Recherche dans sources approuvées et limites de réponse" },
    ],
    metricsToBaseline: [
      { en: "First-response and resolution time", fr: "Temps de première réponse et de résolution" },
      { en: "Escalation and rework rate", fr: "Taux d’escalade et de reprise" },
      { en: "Reviewer effort per request", fr: "Effort de revue par demande" },
    ],
    firstPhase: {
      en: "A controlled triage assistant for one queue, with classification, retrieval, draft recommendations, human review, and quality reporting.",
      fr: "Un assistant de triage contrôlé pour une file, avec classification, recherche, recommandations brouillon, revue humaine et reporting qualité.",
    },
    links: [
      { href: "/services/agentic-ai-workflows", label: { en: "Explore agentic AI workflow automation", fr: "Explorer l’automatisation de flux IA agentique" } },
      { href: "/solutions/customer-support-service-systems", label: { en: "Review customer support system patterns", fr: "Voir les schémas de systèmes support client" } },
      { href: "/contact", label: { en: "Discuss a human-reviewed triage workflow", fr: "Discuter d’un flux de triage avec revue humaine" } },
    ],
  },
  {
    slug: "scattered-operational-data-to-ai-ready-data-foundation",
    title: {
      en: "From scattered operational data to AI-ready data foundation",
      fr: "Des données opérationnelles dispersees à une fondation prête pour l’IA",
    },
    businessSituation: {
      en: "Teams want AI or better analytics, but operational data lives across tools, exports, spreadsheets, and inconsistent definitions.",
      fr: "Les équipes veulent de l’IA ou de meilleures analyses, mais les données opérationnelles vivent dans plusieurs outils, exports, tableurs et définitions incohérentes.",
    },
    workflowProblem: {
      en: "Models, dashboards, and automations cannot be trusted because the input data is fragmented, stale, ambiguous, or owned by no clear team.",
      fr: "Modèles, tableaux de bord et automatisations ne peuvent pas être fiables parce que les données d’entrée sont fragmentées, vieillies, ambiguës ou sans responsable clair.",
    },
    systemPattern: {
      en: "An AI-ready data foundation with source mapping, core entities, quality checks, semantic definitions, access controls, and activation paths.",
      fr: "Une fondation de données prête pour l’IA avec cartographie des sources, entités de base, contrôles qualité, définitions sémantiques, contrôles d’accès et chemins d’activation.",
    },
    dataRequired: [
      { en: "Operational systems, exports, and reporting tables", fr: "Systèmes opérationnels, exports et tables de reporting" },
      { en: "Entity definitions, identifiers, and ownership rules", fr: "Définitions d’entités, identifiants et règles de responsabilité" },
      { en: "Freshness, completeness, and quality expectations", fr: "Attentes de fraîcheur, completude et qualité" },
    ],
    productionControls: [
      { en: "Data quality tests and freshness monitoring", fr: "Tests de qualité données et monitoring de fraîcheur" },
      { en: "Access rules for sensitive or customer data", fr: "Règles d’accès pour données sensibles ou client" },
      { en: "Documented semantic layer and ownership model", fr: "Couche sémantique documentée et modèle de responsabilité" },
    ],
    metricsToBaseline: [
      { en: "Data defect and reconciliation rate", fr: "Taux de defauts données et de rapprochement" },
      { en: "Time to produce trusted datasets", fr: "Temps pour produire des jeux fiables" },
      { en: "Report/model inputs with clear ownership", fr: "Intrants de rapports/modèles avec responsabilité claire" },
    ],
    firstPhase: {
      en: "A governed data model for one AI or decision workflow, with source mapping, quality checks, documentation, and activation-ready outputs.",
      fr: "Un modèle de données gouverné pour un flux IA ou décisionnel, avec cartographie source, contrôles qualité, documentation et sorties prêtes à l’activation.",
    },
    links: [
      { href: "/services/data-platforms", label: { en: "Explore data platforms for AI", fr: "Explorer les plateformes de données pour l’IA" } },
      { href: "/services/ai-readiness-audit", label: { en: "Map AI readiness and data gaps", fr: "Cartographier préparation IA et écarts données" } },
      { href: "/contact", label: { en: "Discuss an AI-ready data foundation", fr: "Discuter d’une fondation de données prête pour l’IA" } },
    ],
  },
  {
    slug: "ai-prototype-to-monitored-production-workflow",
    title: {
      en: "From AI prototype to monitored production workflow",
      fr: "Du prototype IA au flux de production monitore",
    },
    businessSituation: {
      en: "A prototype has shown promise, but it lacks the controls, integrations, evaluation, and ownership required for daily business use.",
      fr: "Un prototype est prometteur, mais il manque les contrôles, intégrations, évaluations et responsabilités requis pour un usage business quotidien.",
    },
    workflowProblem: {
      en: "The prototype works in a demo path but breaks down around edge cases, review queues, data changes, user trust, and operating handoff.",
      fr: "Le prototype fonctionne en demonstration, mais se fragilise avec les cas limites, files de revue, changements de données, confiance utilisateur et transfert opérationnel.",
    },
    systemPattern: {
      en: "A production workflow that wraps the AI capability with evaluation, monitoring, human review, integration, fallback behavior, and ownership.",
      fr: "Un flux de production qui entoure la capacité IA avec évaluation, monitoring, revue humaine, intégration, comportements de repli et responsabilité.",
    },
    dataRequired: [
      { en: "Prototype prompts, model behavior, and test examples", fr: "Prompts du prototype, comportement modèle et exemples de test" },
      { en: "Real workflow inputs, outputs, and exception cases", fr: "Intrants, sorties et cas d’exception du vrai flux" },
      { en: "User roles, approval paths, and launch constraints", fr: "Rôles utilisateurs, chemins d’approbation et contraintes de lancement" },
    ],
    productionControls: [
      { en: "Evaluation harness and launch acceptance criteria", fr: "Harnais d’évaluation et critères d’acceptation au lancement" },
      { en: "Monitoring for quality, usage, latency, and failures", fr: "Monitoring qualité, usage, latence et défaillances" },
      { en: "Fallback logic, ownership, and support cadence", fr: "Logique de repli, responsabilité et cadence de support" },
    ],
    metricsToBaseline: [
      { en: "Prototype accuracy against real examples", fr: "Precision du prototype sur exemples réels" },
      { en: "Human review effort and override rate", fr: "Effort de revue humaine et taux de correction" },
      { en: "Workflow adoption and production incidents", fr: "Adoption du flux et incidents de production" },
    ],
    firstPhase: {
      en: "A production-readiness rebuild for one workflow, with evaluation, integration plan, monitoring model, review logic, and launch criteria.",
      fr: "Une reconstruction de préparation production pour un flux, avec évaluation, plan d’intégration, modèle de monitoring, logique de revue et critères de lancement.",
    },
    links: [
      { href: "/services/production-ai-systems", label: { en: "Explore production AI systems", fr: "Explorer les systèmes IA de production" } },
      { href: "/insights/production-grade-ai", label: { en: "Read what production-grade AI requires", fr: "Lire ce qu’exige l’IA de production" } },
      { href: "/contact", label: { en: "Discuss moving a prototype to production", fr: "Discuter du passage d’un prototype en production" } },
    ],
  },
] as const satisfies readonly WorkPattern[];

export const workSurfaceLinks = [
  {
    href: "/services",
    title: { en: "Explore production AI systems", fr: "Explorer les systèmes IA de production" },
    desc: {
      en: "Map the pattern to production AI systems, workflow automation, data platforms, decision intelligence, or readiness mapping.",
      fr: "Relier le schéma aux systèmes IA de production, automatisations de flux, plateformes de données, intelligence décisionnelle ou diagnostic de préparation.",
    },
  },
  {
    href: "/industries",
    title: { en: "Explore industry patterns", fr: "Explorer les schémas par industrie" },
    desc: {
      en: "See how AI, data, and decision systems apply across retail, CPG, franchise, professional services, and B2B services.",
      fr: "Voir comment IA, données et systèmes décisionnels s’appliquent au retail, CPG, franchise, services professionnels et services B2B.",
    },
  },
  {
    href: "/contact",
    title: { en: "Open a production-fit conversation", fr: "Ouvrir une conversation de cadrage production" },
    desc: {
      en: "Describe the workflow, data challenge, or prototype you need to turn into operating capability.",
      fr: "Décrire le flux, le défi de données ou le prototype à transformer en capacité opérationnelle.",
    },
  },
] as const;

export const getDefaultProofBarItems = (locale: Locale): ProofBarItem[] => [
  {
    code: "01",
    label: locale === "fr" ? "Fondateur" : "Founder-led",
    value: localize(siteContent.proof.founderLedDelivery, locale),
  },
  {
    code: "02",
    label: locale === "fr" ? "Expérience" : "Experience",
    value: localize(siteContent.proof.selectedOperatorExperience, locale),
  },
  {
    code: "03",
    label: locale === "fr" ? "Exécution" : "Execution",
    value:
      locale === "fr"
        ? "Stratégie, architecture, implémentation, évaluation et transfert restent connectés du cadrage à la production."
        : "Strategy, architecture, implementation, evaluation, and handoff stay connected from scope to production.",
  },
  {
    code: "04",
    label: locale === "fr" ? "Expertise" : "Expertise",
    value:
      locale === "fr"
        ? "IA, données, automatisation de flux et systèmes décisionnels sont traites comme une seule capacité opérationnelle."
        : "AI, data, workflow automation, and decision-system design are treated as one operating capability.",
  },
  {
    code: "05",
    label: locale === "fr" ? "Marche" : "Market",
    value:
      locale === "fr"
        ? "Basee au Canada, Vert AI sert des opérateurs nord-américains qui ont besoin de valeur mesurable."
        : "Canada-based, serving North American operators that need measurable operating value.",
  },
];

export const localizeProofText = (text: LocalizedText, locale: Locale) => localize(text, locale);
